/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { css, jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/custom-theme-button';
import AddIcon from '@atlaskit/icon/core/add';
import CloseIcon from '@atlaskit/icon/core/close';
import { SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import { type TriggerProps } from '@atlaskit/popup';
import { Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { TopNavButton } from '@atlassian/navigation-system/experimental/top-nav-button';

import { ANALYTICS_CHANNEL, TRIGGER_BUTTON_TEST_ID } from '../../constants';

const triggerButton = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// below 768px https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples#media-queries
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles
		'@media not all and (min-width: 48rem)': {
			display: 'block',

			// to match with the icon button spacing
			paddingLeft: token('space.100', '8px'),
			paddingRight: token('space.100', '8px'),
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'> span > span': {
				// enforce icon has no extra padding
				paddingLeft: 0,
				paddingRight: 0,
			},
		},
	},
});

/**
 * Refactor this whole logic after T-25, we should use the proper Create component instead.
 * https://jplat.atlassian.net/browse/BLU-7401
 */
const nav3ButtonTextStyles = css({
	// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'@media not all and (min-width: 48rem)': {
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover': {
				paddingLeft: token('space.100', '8px'),
				paddingRight: token('space.100', '8px'),
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(1)': {
				marginLeft: 0,
				marginRight: 0,
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(2)': {
				display: 'none',
			},
		},
	},
});

export type TriggerButtonProps = {
	triggerProps: TriggerProps;
	children: string;
	onClick: () => void;
	isOpen: boolean;
	triggerButtonSpotlightTarget?: string;
	enableTriggerPulse?: boolean;
	testId?: string;
	product: string;
	isNav4Enabled?: boolean;
	/**
	 * @deprecated this is the temporary fix for supporting nav3 custom theming in confluence
	 */
	buttonTheme?: (
		current: any,
		props: any,
	) => {
		buttonStyles: any;
		spinnerStyles: any;
	};
};
export function TriggerButton({
	triggerProps,
	testId,
	onClick,
	children,
	isOpen,
	triggerButtonSpotlightTarget,
	enableTriggerPulse,
	product,
	isNav4Enabled = true,
	buttonTheme,
}: TriggerButtonProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onButtonClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'universalCreateTriggerButton',
			eventType: 'ui',
			tags: ['navigationExperiences'],
			attributes: {
				product,
			},
		});

		analyticsEvent.fire(ANALYTICS_CHANNEL);

		onClick();
	}, [createAnalyticsEvent, onClick, product]);

	const CreateIcon = isOpen ? CloseIcon : AddIcon;

	const button = isNav4Enabled ? (
		<Tooltip content={children} isScreenReaderAnnouncementDisabled position="bottom">
			{/* disabling the tooltip content being announced as it's the same as the children content */}
			<TopNavButton
				{...triggerProps}
				appearance="primary"
				onClick={onButtonClick}
				isSelected={isOpen}
				iconBefore={(iconProps) => <CreateIcon {...iconProps} label="" />}
				testId={testId || TRIGGER_BUTTON_TEST_ID}
				aria-label={children}
			>
				<Show above="sm">{children}</Show>
			</TopNavButton>
		</Tooltip>
	) : (
		<div css={nav3ButtonTextStyles}>
			<Button
				{...triggerProps}
				appearance="primary"
				theme={buttonTheme}
				onClick={onButtonClick}
				iconBefore={<CreateIcon label="" data-vc="global-content-create-button" />}
				testId={testId || TRIGGER_BUTTON_TEST_ID}
				aria-label={children}
				isSelected={isOpen}
			>
				<Show above="sm">{children}</Show>
			</Button>
		</div>
	);
	return (
		<div css={triggerButton}>
			{triggerButtonSpotlightTarget ? (
				<SpotlightTarget name={triggerButtonSpotlightTarget}>
					<SpotlightPulse radius={3} pulse={enableTriggerPulse}>
						{button}
					</SpotlightPulse>
				</SpotlightTarget>
			) : (
				button
			)}
		</div>
	);
}
